import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { standaloneAlterationsAPI, clientsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Plus, Trash2, Scissors, User, Zap, Loader2, Check,
} from 'lucide-react';

const GARMENT_LABELS = {
  jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta', pyjama: 'Pyjama',
  waistcoat: 'Waistcoat', dress: 'Dress', other: 'Other',
};
const ALT_TYPE_LABELS = {
  shorten: 'Shorten', lengthen: 'Lengthen', take_in: 'Take In', let_out: 'Let Out',
  reshape: 'Reshape', reposition: 'Reposition', reline: 'Reline', patch: 'Patch',
  zipper: 'Zipper', button_fix: 'Button Fix', other: 'Other',
};
const AREA_LABELS = {
  chest: 'Chest', waist: 'Waist', hips: 'Hips', shoulders: 'Shoulders', sleeves: 'Sleeves',
  jacket_length: 'Jacket Length', trouser_length: 'Trouser Length', trouser_waist: 'Trouser Waist',
  collar: 'Collar', cuffs: 'Cuffs', lapels: 'Lapels', lining: 'Lining',
  seat: 'Seat', inseam: 'Inseam', outseam: 'Outseam', other: 'Other',
};

export default function NewAlterationPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // Client selection
  const [clientMode, setClientMode] = useState('walk_in'); // 'existing' or 'walk_in'
  const [clientSearch, setClientSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [walkInName, setWalkInName] = useState('');
  const [walkInPhone, setWalkInPhone] = useState('');

  // Garment info
  const [garmentType, setGarmentType] = useState('');
  const [garmentDescription, setGarmentDescription] = useState('');
  const [garmentBrand, setGarmentBrand] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
  const [requiredByDate, setRequiredByDate] = useState('');
  const [isExpress, setIsExpress] = useState(false);
  const [notes, setNotes] = useState('');

  // Alteration items
  const [items, setItems] = useState([]);
  const [chargesMatrix, setChargesMatrix] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    standaloneAlterationsAPI.meta().then(r => setMeta(r.data)).catch(() => {});
    standaloneAlterationsAPI.charges().then(r => setChargesMatrix(r.data || [])).catch(() => {});
  }, []);

  // Client search
  useEffect(() => {
    if (clientMode === 'existing' && clientSearch.length >= 2) {
      clientsAPI.list({ search: clientSearch, limit: 10 }).then(r => setClients(r.data?.clients || r.data || [])).catch(() => {});
    }
  }, [clientSearch, clientMode]);

  const addItem = () => {
    setItems(prev => [...prev, {
      id: Date.now(),
      alteration_type: '',
      garment_area: '',
      description: '',
      customer_charge: 0,
      estimated_hours: 0,
    }]);
  };

  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      // Auto-fill from charges matrix when type + area selected
      if ((field === 'alteration_type' || field === 'garment_area') && updated.alteration_type && updated.garment_area) {
        const match = chargesMatrix.find(c =>
          c.alteration_type === updated.alteration_type &&
          c.garment_area === updated.garment_area &&
          (c.garment_type === garmentType || c.garment_type === 'other') &&
          c.is_active !== false
        );
        if (match) {
          updated.customer_charge = match.customer_charge;
          updated.estimated_hours = match.estimated_hours;
        }
      }
      return updated;
    }));
  };

  const totalCharge = items.reduce((s, i) => s + (i.customer_charge || 0), 0);
  const totalHours = items.reduce((s, i) => s + (i.estimated_hours || 0), 0);

  const canSubmit = (
    (clientMode === 'existing' ? selectedClient : walkInName.trim()) &&
    garmentType && items.length > 0
  );

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        client_id: clientMode === 'existing' ? selectedClient?.id : null,
        walk_in_name: clientMode === 'walk_in' ? walkInName : null,
        walk_in_phone: clientMode === 'walk_in' ? walkInPhone : null,
        garment_type: garmentType,
        garment_description: garmentDescription,
        garment_brand: garmentBrand || null,
        received_date: receivedDate,
        required_by_date: requiredByDate || null,
        is_express: isExpress,
        notes: notes || null,
        items: items.map(i => ({
          alteration_type: i.alteration_type,
          garment_area: i.garment_area,
          description: i.description || '',
          customer_charge: i.customer_charge || 0,
          estimated_hours: i.estimated_hours || 0,
        })),
      };
      const res = await standaloneAlterationsAPI.create(payload);
      toast.success(`Alteration ${res.data.alteration_number} created!`);
      navigate(`/app/alterations/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="new-alteration-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-lg" onClick={() => navigate('/app/alterations')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--brand-navy)' }}>New Alteration Job</h1>
          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Walk-in or external garment alteration</p>
        </div>
      </div>

      {/* Client Selection */}
      <Card className="rounded-xl border bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <User className="w-4 h-4" /> Client
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Button variant={clientMode === 'walk_in' ? 'default' : 'outline'} size="sm" className="text-xs rounded-lg"
              style={clientMode === 'walk_in' ? { backgroundColor: 'var(--brand-navy)' } : {}}
              onClick={() => { setClientMode('walk_in'); setSelectedClient(null); }}
              data-testid="walk-in-mode-btn">Walk-in</Button>
            <Button variant={clientMode === 'existing' ? 'default' : 'outline'} size="sm" className="text-xs rounded-lg"
              style={clientMode === 'existing' ? { backgroundColor: 'var(--brand-navy)' } : {}}
              onClick={() => { setClientMode('existing'); setWalkInName(''); setWalkInPhone(''); }}
              data-testid="existing-client-mode-btn">Existing Client</Button>
          </div>
          {clientMode === 'walk_in' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={walkInName} onChange={e => setWalkInName(e.target.value)}
                  placeholder="Client name" className="h-9 text-sm mt-1" data-testid="walk-in-name" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={walkInPhone} onChange={e => setWalkInPhone(e.target.value)}
                  placeholder="Phone number" className="h-9 text-sm mt-1" data-testid="walk-in-phone" />
              </div>
            </div>
          ) : (
            <div>
              {selectedClient ? (
                <div className="flex items-center gap-2 p-2 rounded-lg border" style={{ backgroundColor: 'var(--surface-2)' }}>
                  <User className="w-4 h-4" style={{ color: 'var(--brand-navy)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
                    {selectedClient.first_name} {selectedClient.last_name}
                  </span>
                  <span className="text-xs ml-1" style={{ color: 'var(--ink-muted)' }}>{selectedClient.phone}</span>
                  <Button variant="ghost" size="sm" className="ml-auto text-xs h-7"
                    onClick={() => { setSelectedClient(null); setClientSearch(''); }}>Change</Button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                  <Input value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                    placeholder="Search clients..." className="pl-9 h-9 text-sm" data-testid="client-search" />
                  {clients.length > 0 && clientSearch.length >= 2 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-40 overflow-auto">
                      {clients.map(c => (
                        <button key={c.id} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex justify-between"
                          onClick={() => { setSelectedClient(c); setClients([]); setClientSearch(''); }}
                          data-testid={`client-option-${c.id}`}>
                          <span>{c.first_name} {c.last_name}</span>
                          <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{c.phone}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Garment Info */}
      <Card className="rounded-xl border bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Scissors className="w-4 h-4" /> Garment Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Garment Type *</Label>
              <Select value={garmentType} onValueChange={setGarmentType}>
                <SelectTrigger className="h-9 text-sm mt-1" data-testid="garment-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(GARMENT_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Brand</Label>
              <Input value={garmentBrand} onChange={e => setGarmentBrand(e.target.value)}
                placeholder="e.g. Zara, Raymond" className="h-9 text-sm mt-1" data-testid="garment-brand" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={garmentDescription} onChange={e => setGarmentDescription(e.target.value)}
              placeholder="e.g. Client's own navy blazer, not H&S made" className="text-sm mt-1 min-h-[60px]"
              data-testid="garment-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Received Date *</Label>
              <Input type="date" value={receivedDate} onChange={e => setReceivedDate(e.target.value)}
                className="h-9 text-sm mt-1" data-testid="received-date" />
            </div>
            <div>
              <Label className="text-xs">Required By</Label>
              <Input type="date" value={requiredByDate} onChange={e => setRequiredByDate(e.target.value)}
                className="h-9 text-sm mt-1" data-testid="required-by-date" />
            </div>
          </div>
          {/* Express toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border"
            style={{ borderColor: isExpress ? 'rgba(217,119,6,0.3)' : 'rgba(27,42,74,0.08)', backgroundColor: isExpress ? 'rgba(217,119,6,0.04)' : 'transparent' }}>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" style={{ color: isExpress ? '#D97706' : 'var(--ink-muted)' }} />
              <span className="text-sm font-medium" style={{ color: isExpress ? '#D97706' : 'var(--navy-ink)' }}>Express / Rush</span>
            </div>
            <button onClick={() => setIsExpress(!isExpress)}
              className="w-11 h-6 rounded-full relative transition-colors flex-shrink-0"
              style={{ backgroundColor: isExpress ? '#D97706' : 'rgba(27,42,74,0.15)' }}
              data-testid="express-toggle">
              <span className="w-5 h-5 bg-white rounded-full absolute top-0.5 shadow-sm transition-all"
                style={{ left: isExpress ? '22px' : '2px' }} />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Alteration Items */}
      <Card className="rounded-xl border bg-white shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Alteration Items</CardTitle>
            <Button size="sm" variant="outline" className="gap-1 text-xs rounded-lg h-8"
              onClick={addItem} data-testid="add-item-btn">
              <Plus className="w-3 h-3" /> Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 && (
            <p className="text-sm text-center py-4" style={{ color: 'var(--ink-muted)' }}>
              No items yet. Click "Add Item" to start.
            </p>
          )}
          {items.map((item, idx) => (
            <div key={item.id} className="p-3 rounded-lg border space-y-2" style={{ borderColor: 'rgba(27,42,74,0.1)' }}
              data-testid={`alt-item-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Item {idx + 1}</span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeItem(item.id)}>
                  <Trash2 className="w-3 h-3 text-red-500" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px]">Alteration Type</Label>
                  <Select value={item.alteration_type} onValueChange={v => updateItem(item.id, 'alteration_type', v)}>
                    <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`alt-type-${idx}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ALT_TYPE_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Garment Area</Label>
                  <Select value={item.garment_area} onValueChange={v => updateItem(item.id, 'garment_area', v)}>
                    <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`alt-area-${idx}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(AREA_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px]">Charge (₹)</Label>
                  <Input type="number" value={item.customer_charge} className="h-8 text-xs mt-0.5"
                    onChange={e => updateItem(item.id, 'customer_charge', parseFloat(e.target.value) || 0)}
                    data-testid={`charge-${idx}`} />
                </div>
                <div>
                  <Label className="text-[10px]">Est. Hours</Label>
                  <Input type="number" step="0.25" value={item.estimated_hours} className="h-8 text-xs mt-0.5"
                    onChange={e => updateItem(item.id, 'estimated_hours', parseFloat(e.target.value) || 0)}
                    data-testid={`hours-${idx}`} />
                </div>
                <div>
                  <Label className="text-[10px]">Notes</Label>
                  <Input value={item.description} className="h-8 text-xs mt-0.5" placeholder="Optional"
                    onChange={e => updateItem(item.id, 'description', e.target.value)} />
                </div>
              </div>
            </div>
          ))}

          {items.length > 0 && (
            <>
              <Separator />
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Total Charge</span>
                    <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }} data-testid="total-charge">
                      ₹{totalCharge.toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>Est. Hours</span>
                    <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }} data-testid="total-hours">
                      {totalHours}h
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="rounded-xl border bg-white shadow-sm">
        <CardContent className="pt-4">
          <Label className="text-xs">Notes</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Any additional notes..." className="text-sm mt-1 min-h-[60px]" data-testid="job-notes" />
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex justify-end gap-3 pb-6">
        <Button variant="outline" className="rounded-lg" onClick={() => navigate('/app/alterations')}>Cancel</Button>
        <Button className="gap-1.5 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
          disabled={!canSubmit || submitting} onClick={handleSubmit} data-testid="confirm-alteration-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Confirm Alteration Job
        </Button>
      </div>
    </div>
  );
}
